import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Dropdown, Form, Input, FormInstance, Menu, Select, Tooltip } from 'antd';
import ProjectApplicationDefinitionEditVisualStore, {
    FieldSettings
} from '../stores/ProjectApplicationDefinitionEditVisualStore';
import { InputBinding, InputMeta, BindingType } from '../types';
import { HasPermission, hasPermission, hasObjectPermission } from '../../authorization/components/HasPermission';
import { AppPermissions } from '../../authorization/Permissions';
import CodeBusinessRulePreview from './CodeBusinessRulePreview';
import { SelectWhenScroll } from '../../common/components/SelectWhenScroll';
import { AppstoreAddOutlined, MoreOutlined, QuestionCircleOutlined, SettingOutlined } from '@ant-design/icons';
import SelectMultipleTagsDialog from './SelectMultipleTagsDialog';
import AuthContext from '../../authorization/AuthContext';
import ApplicationFieldSettingsModal from './ApplicationFieldSettingsModal';
import {
    checkboxConstantDescription,
    checkboxConstantError,
    hasInvalidCheckboxConstant
} from '../misc/checkboxConstant';

const Option = Select.Option;
const InputGroup = Input.Group;
const OptGroup = Select.OptGroup;

type SelectMultipleTagsDialogProps = {
    add: (defaultValue?: unknown, insertIndex?: number | undefined) => void;
    remove: (index: number | number[]) => void;
    inputId: string;
};

interface Props {
    store: ProjectApplicationDefinitionEditVisualStore;
    form: FormInstance;
    input: InputMeta;
    inputFormKey: string;
    bindings: InputBinding[];
    index: number;
    handleInputValuesChange: (inputId: string) => void;
    previewInput: (inputId: string, bindingIndex: number) => void;
    allowFieldSettings: boolean;
    onFieldSettingsChange?: () => void;
    useConditionalBindingsLayout?: boolean;
}

const ApplicationInputBindingsRenderer: React.FC<Props> = ({
    store,
    form,
    input,
    inputFormKey,
    bindings,
    index,
    handleInputValuesChange,
    previewInput,
    allowFieldSettings,
    onFieldSettingsChange,
    useConditionalBindingsLayout = false
}) => {
    // value uesed purely for triggereing component  rerendering
    const [disabledTag, setDisabledTag] = React.useState(false);
    const [isSelectMultipleTagsDialogVisible, setIsSelectMultipleTagsDialogVisible] = React.useState(false);
    const [selectMultipleTagsDialogProps, setSelectMultipleTagsDialogProps] =
        React.useState<SelectMultipleTagsDialogProps>();
    const [selectedField, setSelectedField] = React.useState<InputMeta | null>(null);

    const authContext = React.useContext(AuthContext);
    const canEdit =
        hasPermission(authContext.permissions, AppPermissions.CanEditIotaApplications) ||
        hasObjectPermission(
            authContext.objectPermissions,
            store.currentProject?.id,
            AppPermissions.CanEditIotaApplications
        );
    const canEditFieldSettings = allowFieldSettings && canEdit && !store.isInputDisabled;

    const includeDisabledTag = (binding: InputBinding, inputIndex: number) => {
        if (binding.type === 'tag') {
            const val = form.getFieldValue(inputFormKey);

            return store.isTagDisabled(val[inputIndex].value as string);
        }
        return false;
    };

    const handleChange = (inputIndex: number) => {
        form.setFields([
            {
                name: [inputFormKey, inputIndex, 'value'],
                value: null,
                errors: [],
                validating: false
            }
        ]);
        handleInputValuesChange(inputFormKey);
    };

    const getValidationRules = (inputMeta: InputMeta, bindingIndex?: number) => {
        const booleanRule =
            bindingIndex !== undefined && inputMeta.type === 'checkbox'
                ? [
                      {
                          validator(_: {}, value: string | null) {
                              const type = form.getFieldValue([inputFormKey, bindingIndex, 'type']);

                              return hasInvalidCheckboxConstant(inputMeta, { type, value })
                                  ? Promise.reject(checkboxConstantError)
                                  : Promise.resolve();
                          }
                      }
                  ]
                : [];

        return inputMeta.validation && inputMeta.validation.required
            ? [
                  {
                      required: inputMeta.validation && (inputMeta.validation.required as boolean),
                      message: 'Please input binding information'
                  },
                  () => ({
                      validator(_: {}, value: string | null) {
                          if (value && value.trim() === '') {
                              return Promise.reject('Do not leave empty values.');
                          }
                          return Promise.resolve();
                      }
                  }),
                  ...booleanRule
              ]
            : booleanRule;
    };

    const description = input.type === 'checkbox' ? checkboxConstantDescription : input.description;
    const descriptionIcon = description ? (
        <Tooltip title={description}>
            <QuestionCircleOutlined
                style={{ marginLeft: 8, color: '#9ba0aa' }}
                data-id={`iota-inputs-description-${inputFormKey}`}
            />
        </Tooltip>
    ) : null;

    const getDropdownForTypes = (isDisabled: boolean, i: number) => {
        const shouldDisable = isDisabled || !canEdit;

        return (
            <SelectWhenScroll
                disabled={shouldDisable}
                data-id={`iota-inputs-type-${inputFormKey}-${i}`}
                style={{ width: '100%' }}
                onChange={() => handleChange(i)}
            >
                <Option value={BindingType.constant} data-id={`iota-inputs-type-${inputFormKey}-${i}-constant`}>
                    Constant
                </Option>
                <Option value={BindingType.tag} data-id={`iota-inputs-type-${inputFormKey}-${i}-tag`}>
                    Rule Tag
                </Option>
                <Option value={BindingType.variable} data-id={`iota-inputs-type-${inputFormKey}-${i}-variable`}>
                    Environment Variable
                </Option>
                <Option value={BindingType.codeRule} data-id={`iota-inputs-type-${inputFormKey}-${i}-coderule`}>
                    Code Business Rule
                </Option>
            </SelectWhenScroll>
        );
    };

    const getValueInput = (binding: InputBinding, inputIndex: number, subIndex: number, isInputDisabled?: boolean) => {
        const shouldDisable = isInputDisabled || !canEdit;

        let valueInput = <Input disabled={shouldDisable} data-id={`iota-inputs-value-${inputFormKey}-${inputIndex}`} />;

        if (binding.type === BindingType.tag) {
            const groups = store.tagGroups;
            valueInput = (
                <SelectWhenScroll
                    disabled={shouldDisable}
                    data-id={`iota-inputs-value-${inputFormKey}-${inputIndex}`}
                    style={{ width: '100%' }}
                    showSearch
                    onChange={() => setDisabledTag(!disabledTag)}
                >
                    {groups.map(g => (
                        <OptGroup key={g.id} label={g.name}>
                            {store.getTagsForGroup(g.id).map(x => (
                                <Option
                                    data-id={`iota-inputs-value-${inputFormKey}-${inputIndex}-${x}`}
                                    key={x}
                                    value={x}
                                >
                                    {x}
                                </Option>
                            ))}
                        </OptGroup>
                    ))}
                </SelectWhenScroll>
            );
        }

        if (binding.type === BindingType.variable) {
            valueInput = (
                <SelectWhenScroll
                    disabled={shouldDisable}
                    data-id={`iota-inputs-value-${inputFormKey}-${inputIndex}`}
                    style={{ width: '100%' }}
                >
                    {store.environmentVariables.map(x => (
                        <Option
                            data-id={`iota-inputs-value-${inputFormKey}-${inputIndex}-${x.id}`}
                            key={x.id}
                            value={x.id}
                        >
                            {x.name}
                        </Option>
                    ))}
                </SelectWhenScroll>
            );
        }

        if (binding.type === BindingType.codeRule) {
            valueInput = (
                <CodeBusinessRulePreview
                    isDisabled={shouldDisable}
                    code={binding.value as string}
                    dataId={`iota-inputs-value-${inputFormKey}-${index}`}
                    onSave={c => {
                        const vals = form.getFieldValue(inputFormKey);
                        vals[subIndex].value = c;
                        const obj = Object.create(null);
                        obj[inputFormKey] = vals;
                        form.setFieldsValue(obj);
                        handleInputValuesChange(inputFormKey);
                    }}
                />
            );
        }

        return valueInput;
    };

    const handleFieldSettingsSubmit = (values: FieldSettings) => {
        if (!canEditFieldSettings) {
            return;
        }

        store.setFieldSettings(input.id, values);
        onFieldSettingsChange?.();
        setSelectedField(null);
    };

    const fieldSettings = store.getFieldSettings(input);

    const addEmptyBinding = (add: SelectMultipleTagsDialogProps['add']) => {
        add({
            type: BindingType.constant,
            value: ''
        });
    };

    const openSelectMultipleTagsDialog = (
        add: SelectMultipleTagsDialogProps['add'],
        remove: SelectMultipleTagsDialogProps['remove']
    ) => {
        setSelectMultipleTagsDialogProps({
            add,
            remove,
            inputId: input.id
        });
        setIsSelectMultipleTagsDialogVisible(true);
    };

    const openFieldSettings = (event?: React.MouseEvent) => {
        event?.preventDefault();
        event?.stopPropagation();

        if (canEditFieldSettings) {
            setSelectedField(input);
        }
    };

    const renderConditionalLabel = () => {
        const status = !fieldSettings.isVisible ? 'H' : fieldSettings.isRequired ? '*' : '';
        const statusContent =
            status === 'H' ? (
                <Tooltip title="Hidden on client UI">
                    <span data-id={`iota-inputs-hidden-indicator-${inputFormKey}`}>H</span>
                </Tooltip>
            ) : (
                status
            );

        return (
            <div className="conditional-binding-label">
                <span className="conditional-binding-label__status" data-id={`iota-inputs-status-${inputFormKey}`}>
                    {statusContent}
                </span>
                <Tooltip title={input.name}>
                    <span className="conditional-binding-label__name binding-label">{input.name}</span>
                </Tooltip>
                {descriptionIcon}
            </div>
        );
    };

    const renderDefaultLabel = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip title={input.name}>
                <div className="binding-label">{input.name}</div>
            </Tooltip>
            {descriptionIcon}

            {!fieldSettings.isVisible ? (
                <span
                    data-id={`iota-inputs-hidden-indicator-${inputFormKey}`}
                    style={{ color: '#9BA0AA', fontSize: 12, fontWeight: 600 }}
                >
                    H
                </span>
            ) : null}
        </div>
    );

    const renderPreviewAction = (binding: InputBinding, bindingIndex: number) =>
        typeof binding.value === 'string' && binding.value.trim().length ? (
            <Tooltip title="Preview Input">
                <Button
                    data-id={`iota-inputs-preview-${inputFormKey}-${bindingIndex}`}
                    aria-label={`Preview ${input.name}`}
                    type="link"
                    size="small"
                    onClick={() => previewInput(input.id, bindingIndex)}
                >
                    <i className="alpha-icon lg play-icon green" />
                </Button>
            </Tooltip>
        ) : null;

    const renderConditionalActionsMenu = (
        bindingIndex: number,
        fieldsLength: number,
        add: SelectMultipleTagsDialogProps['add'],
        remove: SelectMultipleTagsDialogProps['remove']
    ) => {
        const isPrimaryBinding = bindingIndex === 0;
        const menu = isPrimaryBinding ? (
            <Menu>
                <Menu.Item
                    key="add-tag"
                    data-id={`iota-inputs-menu-add-${inputFormKey}`}
                    disabled={store.isInputDisabled}
                    onClick={() => addEmptyBinding(add)}
                >
                    Add tag
                </Menu.Item>
                <Menu.Item
                    key="add-multiple-tags"
                    data-id={`iota-inputs-menu-add-tags-${inputFormKey}`}
                    disabled={store.isInputDisabled}
                    onClick={() => openSelectMultipleTagsDialog(add, remove)}
                >
                    Add multiple tags
                </Menu.Item>
                {allowFieldSettings && canEdit ? (
                    <Menu.Item
                        key="field-settings"
                        data-id={`iota-inputs-menu-field-settings-${inputFormKey}`}
                        disabled={!canEditFieldSettings}
                        onClick={() => openFieldSettings()}
                    >
                        Field settings
                    </Menu.Item>
                ) : null}
                {fieldsLength > 1 ? (
                    <Menu.Item
                        key="remove-tag"
                        data-id={`iota-inputs-menu-remove-${inputFormKey}-${bindingIndex}`}
                        disabled={store.isInputDisabled}
                        onClick={() => remove(bindingIndex)}
                    >
                        Remove tag
                    </Menu.Item>
                ) : null}
            </Menu>
        ) : (
            <Menu>
                <Menu.Item
                    key="remove-tag"
                    data-id={`iota-inputs-menu-remove-${inputFormKey}-${bindingIndex}`}
                    disabled={store.isInputDisabled}
                    onClick={() => remove(bindingIndex)}
                >
                    Remove tag
                </Menu.Item>
            </Menu>
        );

        return (
            <Tooltip title="More actions">
                <Dropdown overlay={menu} trigger={['click']} placement="bottomRight" disabled={store.isInputDisabled}>
                    <Button
                        data-id={`iota-inputs-more-${inputFormKey}-${bindingIndex}`}
                        type="link"
                        size="small"
                        disabled={store.isInputDisabled}
                        style={{ width: 40, verticalAlign: 'text-bottom', marginBottom: 2, color: '#858585' }}
                    >
                        <MoreOutlined />
                    </Button>
                </Dropdown>
            </Tooltip>
        );
    };

    return (
        <>
            {allowFieldSettings ? (
                <ApplicationFieldSettingsModal
                    field={selectedField}
                    isVisible={!!selectedField}
                    isDisabled={!canEditFieldSettings}
                    initialValues={store.getFieldSettings(selectedField ?? input)}
                    onCancel={() => setSelectedField(null)}
                    onSubmit={handleFieldSettingsSubmit}
                />
            ) : null}

            {selectMultipleTagsDialogProps && (
                <SelectMultipleTagsDialog
                    isVisible={isSelectMultipleTagsDialogVisible}
                    setIsVisible={setIsSelectMultipleTagsDialogVisible}
                    store={store}
                    add={selectMultipleTagsDialogProps.add}
                    remove={selectMultipleTagsDialogProps.remove}
                    inputId={selectMultipleTagsDialogProps.inputId}
                    form={form}
                />
            )}

            <Form.List key={`${inputFormKey}-${index}`} name={inputFormKey}>
                {(fields, { add, remove }) => {
                    return (
                        <InputGroup key={`${inputFormKey}-${index}`} size="default" style={{ marginBottom: 20 }}>
                            {fields.map((field, i) => {
                                const binding = bindings[i];
                                const containerClassName = [
                                    i !== 0 ? 'no-label' : '',
                                    'binding-container',
                                    useConditionalBindingsLayout ? 'conditional-binding-container' : ''
                                ]
                                    .filter(Boolean)
                                    .join(' ');
                                return (
                                    <div key={`${index}-${i}`} className={containerClassName}>
                                        <Form.Item
                                            colon={false}
                                            label={
                                                useConditionalBindingsLayout
                                                    ? renderConditionalLabel()
                                                    : renderDefaultLabel()
                                            }
                                            className={
                                                !useConditionalBindingsLayout && fieldSettings.isRequired
                                                    ? 'label-required'
                                                    : ''
                                            }
                                            style={{ marginBottom: 5 }}
                                        >
                                            <Form.Item
                                                style={{ marginBottom: 5 }}
                                                {...field}
                                                key={`${index}-${i}-type`}
                                                name={[field.name, 'type']}
                                                rules={getValidationRules(input)}
                                            >
                                                {getDropdownForTypes(store.isInputDisabled || false, i)}
                                            </Form.Item>

                                            <Form.Item
                                                {...field}
                                                key={`${index}-${i}-value`}
                                                name={[field.name, 'value']}
                                                rules={getValidationRules(input, i)}
                                                style={{ marginBottom: 5, marginLeft: 10 }}
                                            >
                                                {getValueInput(binding, index, i, store.isInputDisabled)}
                                            </Form.Item>

                                            <HasPermission
                                                entityId={store.currentProject?.id}
                                                permissionClaim={AppPermissions.CanEditIotaApplications}
                                            >
                                                <span>
                                                    {useConditionalBindingsLayout ? (
                                                        <>
                                                            {renderPreviewAction(binding, i)}
                                                            {renderConditionalActionsMenu(
                                                                i,
                                                                fields.length,
                                                                add,
                                                                remove
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                data-id={`iota-inputs-remove-${inputFormKey}-${i}`}
                                                                type="link"
                                                                disabled={store.isInputDisabled || fields.length === 1}
                                                                onClick={() => remove(field.name)}
                                                                size="small"
                                                            >
                                                                <i className="alpha-icon lg minus-icon" />
                                                            </Button>
                                                            {i === fields.length - 1 && (
                                                                <Button
                                                                    data-id={`iota-inputs-add-${inputFormKey}`}
                                                                    type="link"
                                                                    disabled={store.isInputDisabled}
                                                                    onClick={() => addEmptyBinding(add)}
                                                                    size="small"
                                                                >
                                                                    <i className="alpha-icon lg plus-icon" />
                                                                </Button>
                                                            )}
                                                            {i === 0 && (
                                                                <>
                                                                    <Tooltip title="Add Tags">
                                                                        <Button
                                                                            data-id={`iota-inputs-add-tags-${inputFormKey}`}
                                                                            type="link"
                                                                            size="small"
                                                                            style={{
                                                                                width: 40,
                                                                                verticalAlign: 'text-bottom',
                                                                                marginBottom: 2,
                                                                                color: '#858585'
                                                                            }}
                                                                            disabled={store.isInputDisabled}
                                                                            onClick={() =>
                                                                                openSelectMultipleTagsDialog(
                                                                                    add,
                                                                                    remove
                                                                                )
                                                                            }
                                                                        >
                                                                            <AppstoreAddOutlined />
                                                                        </Button>
                                                                    </Tooltip>

                                                                    {allowFieldSettings && canEdit && (
                                                                        <Tooltip title="Field Settings">
                                                                            <Button
                                                                                data-id={`iota-inputs-field-settings-${inputFormKey}`}
                                                                                type="link"
                                                                                size="small"
                                                                                disabled={!canEditFieldSettings}
                                                                                style={{
                                                                                    width: 40,
                                                                                    verticalAlign: 'text-bottom',
                                                                                    marginBottom: 2,
                                                                                    color: '#858585'
                                                                                }}
                                                                                onClick={openFieldSettings}
                                                                            >
                                                                                <SettingOutlined />
                                                                            </Button>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            )}

                                                            {renderPreviewAction(binding, i)}
                                                        </>
                                                    )}
                                                </span>
                                            </HasPermission>

                                            <div className="iota-disabled-rule-tag">
                                                {disabledTag !== undefined && includeDisabledTag(bindings[i], i)
                                                    ? 'All Rules under this Tag are disabled'
                                                    : ''}
                                            </div>
                                        </Form.Item>
                                    </div>
                                );
                            })}
                        </InputGroup>
                    );
                }}
            </Form.List>
        </>
    );
};

export default observer(ApplicationInputBindingsRenderer);
